import type { ForwardedRef } from "react"
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react"
import { createPortal } from "react-dom"

import { cn } from "@productlane/lib/src/cn"

import type { ContextMentionItem } from "./context-mention"
import type {
	SuggestionKeyDownProps,
	SuggestionProps,
} from "@tiptap/suggestion"
import { useSidebarAiChatState } from "../sidebar-ai-chat-state"
import { contextMentionKey } from "./context-mention"
import { ContextMentionIcon } from "./context-mention-icon"
import { flattenContextMentionSections } from "./context-mention-results"
import { useContextMentionResults } from "./use-context-mention-results"

export interface ContextMentionListActions {
	onKeyDown: (props: SuggestionKeyDownProps) => boolean
}

const Row = ({
	item,
	isActive,
	onSelect,
	onHover,
}: {
	item: ContextMentionItem
	isActive: boolean
	onSelect: () => void
	onHover: () => void
}) => {
	const ref = useRef<HTMLButtonElement | null>(null)

	useEffect(() => {
		if (isActive) ref.current?.scrollIntoView({ block: "nearest" })
	}, [isActive])

	return (
		<button
			ref={ref}
			type="button"
			onMouseEnter={onHover}
			onMouseDown={(event) => {
				event.preventDefault()
				onSelect()
			}}
			className={cn(
				"flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
				isActive && "bg-gray-100 dark:bg-gray-800",
			)}
		>
			<ContextMentionIcon
				item={item}
				className="h-3.5 w-3.5 flex-none"
				avatarSize="xs"
			/>
			{item.prefix && (
				<span className="flex-none font-mono text-xs text-tertiary">
					{item.prefix}
				</span>
			)}
			<span className="min-w-0 flex-1 truncate text-sm text-primary">
				{item.label}
			</span>
		</button>
	)
}

const ContextMentionList = forwardRef(
	(
		{ command, query, clientRect }: SuggestionProps,
		ref: ForwardedRef<ContextMentionListActions>,
	) => {
		const scope = useSidebarAiChatState((state) => state.scope)
		const sections = useContextMentionResults(query, scope)
		const items = useMemo(
			() => flattenContextMentionSections(sections),
			[sections],
		)
		const [activeIndex, setActiveIndex] = useState(0)

		useEffect(() => setActiveIndex(0), [query, items.length])

		const select = useCallback(
			(index: number) => {
				const item = items[index]
				if (!item) return
				command({
					id: item.id,
					type: item.type,
					label: item.label,
					prefix: item.prefix ?? null,
					color: item.color ?? null,
					status: item.status ?? null,
					icon: item.icon ?? null,
					imageUrl: item.imageUrl ?? null,
					origin: item.origin ?? null,
				})
			},
			[command, items],
		)

		useImperativeHandle(ref, () => ({
			onKeyDown: ({ event }: SuggestionKeyDownProps) => {
				if (items.length === 0) return false
				if (event.key === "ArrowUp") {
					setActiveIndex((i) => (i + items.length - 1) % items.length)
					return true
				}
				if (event.key === "ArrowDown") {
					setActiveIndex((i) => (i + 1) % items.length)
					return true
				}
				if (event.key === "Enter" || event.key === "Tab") {
					select(activeIndex)
					return true
				}
				return false
			},
		}))

		const rect = clientRect?.()
		if (!rect || typeof document === "undefined") return null

		let index = -1

		return createPortal(
			<div
				className="fixed z-50 w-[320px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-lg bg-white p-1 shadow-lg ring-1 ring-black/10 dark:bg-gray-800 dark:ring-white/10"
				style={{
					left: Math.min(rect.left, window.innerWidth - 332),
					bottom: window.innerHeight - rect.top + 8,
					maxHeight: Math.min(320, Math.max(160, rect.top - 24)),
				}}
			>
				{items.length === 0 ? (
					<div className="px-2 py-3 text-sm text-secondary">
						Nothing matches &quot;{query}&quot;
					</div>
				) : (
					sections.map((section) => (
						<div key={section.key} className="pb-1 last:pb-0">
							<div className="px-2 py-1 text-xs font-medium text-tertiary">
								{section.label}
							</div>
							{section.items.map((item) => {
								index += 1
								const rowIndex = index
								return (
									<Row
										key={contextMentionKey(item)}
										item={item}
										isActive={rowIndex === activeIndex}
										onHover={() => setActiveIndex(rowIndex)}
										onSelect={() => select(rowIndex)}
									/>
								)
							})}
						</div>
					))
				)}
			</div>,
			document.body,
		)
	},
)

export default ContextMentionList
